'use client';

/**
 * Puts the arrival fragment back on the URL after sign-in. What it is and why
 * it is carried this way is in `lib/auth/arrival-fragment.ts` (2026-09-20, the
 * Revent first-touch e2e run: the login screen dropped the fragment a scanned
 * code arrived with, and the person landed on their workspace holding nothing).
 *
 * WHERE IT RUNS. Mounted once per page load in `AppProviders`, ahead of the
 * page, so it runs at the first commit of whatever page the sign-in callbacks
 * landed on. The destination is theirs: an arbitrary same-origin `returnTo`,
 * followed by whatever server redirects that page performs (`/?app=<id>`
 * becomes `/workspace?spawn=content&contentId=...`), so this does not try to
 * predict it. It redeems the stash on the first SIGNED-IN page after the
 * login screen, which is the one property the hop has: the `logged_in` marker
 * cookie is present (the non-httpOnly companion `setSession` writes for
 * exactly this kind of client-side check), and the login screen itself is
 * not under `AppProviders`, so the stash is never eaten before the provider
 * click. A stale stash left by an abandoned login stays where it is until the
 * person signs in within the TTL or the tab closes; it never leaves the tab.
 *
 * HOW IT WRITES: a raw `history.replaceState(null, ...)`, deferred by one
 * microtask. Both halves matter. Next patches `history.replaceState` so an
 * outside caller's URL is copied into the router's `canonicalUrl`
 * (`app-router.js`, `applyUrlFromHistoryPushReplace`), and `HistoryUpdater`
 * rewrites the address bar from `canonicalUrl` on EVERY router state change,
 * prefetches included. That patch is installed in the root `AppRouter`'s own
 * effect, and a parent's effect runs after its children's, so a replaceState
 * issued synchronously from this mount effect would reach the unpatched
 * original: the hash would show for a moment and vanish at the next router
 * action, possibly before the app's iframe had done its handshake. One
 * microtask later the whole passive-effect flush is over, the patch is in
 * place, and the hash becomes part of the URL the router itself preserves.
 * `null` for the state argument is what lets the patch see the call at all:
 * it passes through anything carrying its own `__NA` marker untouched.
 *
 * WHAT READS IT. `widget-bridge.tsx` parses `location.hash` into
 * `host.workspace.params` at each window's `__ready__` push, after that
 * window's iframe has loaded, long after this has run. The hash is left on
 * the URL afterwards on purpose: every window reads it lazily at its own
 * handshake, so stripping it after the first handshake would starve a window
 * whose iframe loaded second. A targeted strip belongs in the bridge.
 */

import { useEffect } from 'react';
import { takeArrivalFragment } from '@/lib/auth/arrival-fragment';
import { adoptArrivalQuery } from '@/lib/auth/arrival-query';

/**
 * The non-httpOnly companion cookie `setSession` writes (lib/auth/session.ts):
 * `logged_in` on the apex, `__Host-logged_in` on a custom domain or named
 * host (A2, lib/auth/cookie-names.ts). Both spellings count, and only a
 * NAME match counts: the earlier `startsWith('logged_in=')` missed the host
 * spelling, so the fragment was never restored on a host (functional
 * review, 2026-09-24), and a substring match would also accept a cookie an
 * app frame planted with the name inside it.
 */
const SIGNED_IN_MARKERS = ['logged_in=', '__Host-logged_in='] as const;

export function hasSignedInMarker(cookie: string): boolean {
  return cookie
    .split(';')
    .some((c) => SIGNED_IN_MARKERS.some((marker) => c.trim().startsWith(marker)));
}

/**
 * The whole behaviour as a function of the window, so a test can drive a
 * jsdom window. True when a fragment was put back on the URL. The path and
 * query are left exactly as they were: only a `#fragment` is added, and only
 * when the page did not already have one.
 */
export function restoreArrivalFragment(win: Window): boolean {
  let signedIn = false;
  try {
    signedIn = hasSignedInMarker(win.document.cookie);
  } catch {
    return false;
  }
  if (!signedIn) return false;
  let storage: Storage | null = null;
  try {
    storage = win.sessionStorage;
  } catch {
    // Opaque origin or blocked storage: nothing could have been parked either.
    return false;
  }
  const fragment = takeArrivalFragment(storage);
  if (!fragment) return false;
  const { location, history } = win;
  if (location.hash) return false;
  try {
    history.replaceState(null, '', `${location.pathname}${location.search}#${fragment}`);
  } catch {
    return false;
  }
  return true;
}

export function ArrivalFragmentRestore() {
  useEffect(() => {
    let cancelled = false;
    // Deferred past the passive-effect flush; see the header for why this
    // must land AFTER the root router has patched `history.replaceState`.
    queueMicrotask(() => {
      if (cancelled) return;
      restoreArrivalFragment(window);
      /* THEN THE QUERY FORM (2026-10-11, lib/auth/arrival-query.ts): a link
         that names a conference or an event in its query
         (`?conference=la-2026`, `?event=<id>`) is turned into the same
         fragment here, on the first page that renders, so the windows read
         one grammar. AFTER the restore, on purpose: a fragment the person
         arrived with (parked across sign-in, just put back) wins over the
         query, and this writes nothing once the address has one. Signed in
         or not: an id in a link is not a session's secret. */
      adoptArrivalQuery(window);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return null;
}
